import React from 'react';

import {Nut_Settings_User} from 'nutshell-core/settings-constants';

import {Stack} from 'shells/layout/stack/stack';
import {Body} from 'shells/typography/body';

import {useUpdateSetting} from '../../../core/use-update-setting';

import {SettingToggle} from '../../licensing/setting-toggle';

import {useGetUserAiFeatureSettings} from './graphql/hooks/use-get-user-ai-feature-settings';
import {getIsFeatureEnabled} from './helpers';

export interface AiFeatureSetting {
    settingName: string;
    name: string;
    description: string;
    learnMoreUrl?: string;
    defaultEnabled: boolean;
}

const AI_FEATURE_SETTINGS: AiFeatureSetting[] = [
    {
        settingName: Nut_Settings_User.SETTING_AI_FOLLOW_UP_TASKS_ENABLED,
        name: 'AI follow-up tasks',
        description: 'Let Nutshell AI create follow-up tasks from summarized calls and meetings',
        defaultEnabled: false,
    },
];

interface Props {
    userId: string;
}

export function UserAiFeatures(props: Props) {
    const {settings, isLoading} = useGetUserAiFeatureSettings(props.userId);
    const updateSetting = useUpdateSetting();

    const [localOverrides, setLocalOverrides] = React.useState<Record<string, boolean>>({});

    React.useEffect(() => {
        setLocalOverrides({});
    }, [settings]);

    if (isLoading) {
        return null;
    }

    const handleToggle = (feature: AiFeatureSetting, currentlyOn: boolean) => {
        const newValue = !currentlyOn;
        setLocalOverrides((prev) => ({...prev, [feature.settingName]: newValue}));

        updateSetting({
            settingName: feature.settingName,
            value: newValue ? '1' : '0',
            isGlobal: false,
            userId: props.userId,
        });
    };

    return (
        <Stack spacing={12}>
            {AI_FEATURE_SETTINGS.map((feature) => {
                const hasOverride = feature.settingName in localOverrides;
                const isOn = hasOverride
                    ? Boolean(localOverrides[feature.settingName])
                    : getIsFeatureEnabled(feature, settings);

                return (
                    <SettingToggle
                        key={feature.settingName}
                        displayName={feature.name}
                        isOn={isOn}
                        onToggle={() => handleToggle(feature, isOn)}
                        infoLink={feature.learnMoreUrl}
                    >
                        <Body color='light'>{feature.description}</Body>
                    </SettingToggle>
                );
            })}
        </Stack>
    );
}
